// assets/pct-edit-book.js
// Pictoirel — unified "Edit Book" tool. Replaces the former standalone
// pct-reorder-spreads.js + pct-swap-photos.js with one popup: a left sidebar
// listing all spreads with a "Rearrange Spreads" toggle (drag or click-to-swap
// while active; a plain click just navigates/previews a spread otherwise), a
// book-styled preview of the current spread on the right where photos can be
// clicked to swap, and a static selection panel showing the currently-selected
// photo. Clicking the book stage while rearranging exits rearrange mode (same
// effect as the old "Done Rearranging" button — no server save, just ends the
// mode and keeps whatever was reordered; the real save still happens at Close).
//
// Reuses spreadMemory + ensureBlueprintFinalizedForCheckout() (design-studio-modal.liquid)
// plus build3DFlipbook()/updateStepper()/validateMainStep() (main-product-builder.liquid).
// Does NOT touch the Fabric.js canvas editor, ds-overview.js, or openAllPages —
// fully independent, own thumbnail cache, own DOM.
//
// State-conflict rule: photo-selection (for swapping) and rearrange mode are
// mutually exclusive. A selected photo is stored as a POSITIONAL reference
// ({spreadIndex, objectIndex}); if spread order changed while that reference
// was still alive, it would silently point at the wrong spread. So entering
// rearrange mode clears any active photo selection, photos are not clickable
// while rearranging, and leaving rearrange mode clears any pending spread-swap
// selection. The two selection states can never coexist.
(function () {
  'use strict';

  var _overlay = null;
  var _sidebarScroll = null;
  var _sidebarList = null;
  var _rearrangeToggle = null;
  var _rearrangeHint = null;
  var _stageWrap = null;
  var _book = null;
  var _stage = null;
  var _selectionEmpty = null;
  var _selectionPanel = null;
  var _selectionThumbImg = null;
  var _selectionText = null;
  var _closeBtn = null;
  var _savingLock = null;
  var _isBuilt = false;
  var _escAttached = false;
  var _saving = false;
  var _confirmingClose = false;

  var _currentSpreadIndex = 0;
  var _selectedPhoto = null;  // { spreadIndex, objectIndex } | null
  var _rearranging = false;
  var _selectedSpreadIdx = null; // spread picked for click-to-swap while rearranging
  var _snapshotMemory = null;
  var _thumbCache = [];
  var _thumbGen = [];      // per-spread generation token — bumped whenever that spread's cache is invalidated, so an in-flight render that started before the invalidation gets discarded instead of clobbering fresher data
  var _thumbPromise = [];  // per-spread in-flight thumbnail render, shared by every caller so repeated renderSidebar() calls don't kick off redundant Fabric renders for the same spread

  var STAGE_W = 800;
  var STAGE_H = 566;
  var SIDEBAR_THUMB_W = 140;
  var SIDEBAR_THUMB_H = 99;

  var style = document.createElement('style');
  style.textContent = [
    '.pct-eb-overlay{position:fixed;inset:0;background:#fbfbfb;z-index:99999996;display:none;flex-direction:column;font-family:"Neue Haas Grotesk",Helvetica,Arial,sans-serif;}',
    '.pct-eb-overlay.is-open{display:flex;}',
    '.pct-eb-header{flex-shrink:0;display:flex;align-items:center;justify-content:space-between;padding:18px 28px;background:#fff;border-bottom:1px solid #eaeaea;}',
    '.pct-eb-title{color:#1a1a1a;font-size:20px;font-weight:800;font-family:"Syne",sans-serif;display:flex;align-items:center;gap:10px;}',
    '.pct-eb-title svg{width:20px;height:20px;color:#d4af37;}',
    '.pct-eb-close{background:#fff;border:1px solid #eaeaea;color:#555;padding:9px 18px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;font-family:"Syne",sans-serif;transition:background .15s,border-color .15s;}',
    '.pct-eb-close:hover:not(:disabled){background:#f9f9f9;border-color:#ccc;}',
    '.pct-eb-close:disabled{opacity:0.6;cursor:default;}',
    '.pct-eb-instructions{flex-shrink:0;padding:14px 28px;display:flex;flex-direction:column;gap:8px;background:#fff;border-bottom:1px solid #eaeaea;}',
    '.pct-eb-note{margin:0;padding:10px 12px;border:1px solid rgba(212,175,55,0.35);border-radius:8px;background:#fffaf0;color:#5f5433;font-size:12.5px;line-height:1.5;font-weight:600;}',
    '.pct-eb-note b{color:#8a6d1e;}',
    '.pct-eb-body{flex:1 1 auto;min-height:0;display:flex;gap:0;background:#fbfbfb;}',

    '.pct-eb-sidebar{width:196px;flex-shrink:0;display:flex;flex-direction:column;padding:16px 12px 20px;background:#fff;border-right:1px solid #eaeaea;}',
    '.pct-eb-rearrange-toggle{width:100%;border:1.5px solid #eaeaea;background:#fff;color:#1a1a1a;border-radius:999px;padding:10px 14px;font-size:12.5px;font-weight:700;font-family:"Syne",sans-serif;cursor:pointer;margin-bottom:6px;transition:background .15s,border-color .15s,color .15s;}',
    '.pct-eb-rearrange-toggle:hover:not(:disabled):not(.is-active){border-color:#1a1a1a;}',
    '.pct-eb-rearrange-toggle.is-active{background:#d4af37;color:#fff;border-color:#d4af37;}',
    '.pct-eb-rearrange-toggle:disabled{opacity:0.4;cursor:not-allowed;}',
    '.pct-eb-rearrange-hint{display:none;margin:0 0 12px;padding:8px 10px;border:1px solid rgba(212,175,55,0.35);border-radius:8px;background:#fffaf0;color:#5f5433;font-size:11.5px;line-height:1.5;font-weight:600;}',
    '.pct-eb-rearrange-hint.is-visible{display:block;}',
    '.pct-eb-sidebar-scroll{flex:1 1 auto;min-height:0;overflow-y:auto;}',

    '.pct-eb-sidebar-item{position:relative;cursor:pointer;border-radius:6px;overflow:hidden;border:2px solid #eaeaea;margin-bottom:12px;background:#f4f4f4;transition:border-color .12s,box-shadow .12s;}',
    '.pct-eb-sidebar-item[draggable="true"]{cursor:grab;}',
    '.pct-eb-sidebar-item:hover{border-color:#cfcfcf;}',
    '.pct-eb-sidebar-item.is-active{border-color:#1a1a1a;}',
    '.pct-eb-sidebar-item.is-rearrange-selected{border-color:#d4af37;box-shadow:0 0 0 3px rgba(212,175,55,0.25);}',
    '.pct-eb-sidebar-item img{width:100%;display:block;aspect-ratio:' + SIDEBAR_THUMB_W + '/' + SIDEBAR_THUMB_H + ';object-fit:cover;}',
    '.pct-eb-sidebar-num{position:absolute;bottom:4px;left:4px;background:rgba(0,0,0,0.55);color:#fff;font-size:10px;font-weight:700;padding:2px 6px;border-radius:3px;}',
    '.pct-eb-sidebar-marker{position:absolute;top:4px;right:4px;width:10px;height:10px;border-radius:50%;background:#d4af37;box-shadow:0 0 0 2px #fff;display:none;}',
    '.pct-eb-sidebar-item.has-selection .pct-eb-sidebar-marker{display:block;}',
    '.pct-eb-swap-badge{position:absolute;top:50%;left:50%;width:36px;height:36px;margin:-18px 0 0 -18px;background:rgba(20,20,20,0.72);border-radius:50%;display:none;align-items:center;justify-content:center;z-index:65;pointer-events:none;}',
    '.pct-eb-sidebar-item.is-rearrange-selected .pct-eb-swap-badge{display:flex;}',
    '.pct-eb-photo-target.is-selected .pct-eb-swap-badge{display:flex;}',
    '.pct-eb-swap-badge svg{width:18px;height:18px;animation:pct-eb-spin 1.1s linear infinite;}',
    '@keyframes pct-eb-spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}',
    '@keyframes pct-eb-popin{0%{transform:scale(0);opacity:0;}100%{transform:scale(1);opacity:1;}}',

    '.pct-eb-stage-wrap{flex:1 1 auto;min-width:0;min-height:0;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:12px;}',
    '.pct-eb-book{position:relative;width:' + STAGE_W + 'px;height:' + STAGE_H + 'px;flex-shrink:0;border-radius:4px;overflow:hidden;background:#fff;box-shadow:0 24px 60px rgba(30,26,20,0.22),0 6px 18px rgba(30,26,20,0.12);transform-origin:center center;}',
    '.pct-eb-book.is-locked{cursor:pointer;}',
    '.pct-eb-book.is-locked .pct-eb-photo-target{cursor:default;}',
    '.pct-eb-stage{position:relative;width:100%;height:100%;}',
    '.pct-eb-book-lighting{position:absolute;inset:0;pointer-events:none;z-index:55;background:linear-gradient(to right, rgba(0,0,0,0.04) 0%, rgba(255,255,255,0) 4%, rgba(255,255,255,0.15) 45%, rgba(0,0,0,0.15) 48%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.15) 52%, rgba(255,255,255,0.15) 55%, rgba(255,255,255,0) 96%, rgba(0,0,0,0.04) 100%);}',
    '.pct-eb-spine-line{position:absolute;top:0;bottom:0;left:50%;width:2px;background:linear-gradient(to right, rgba(0,0,0,0.2), rgba(0,0,0,0.05));filter:drop-shadow(0px 0px 1px rgba(255,255,255,0.4));transform:translateX(-50%);z-index:60;}',
    '.pct-eb-obj{position:absolute;}',
    '.pct-eb-obj img{position:absolute;pointer-events:none;max-width:none;max-height:none;}',
    '.pct-eb-photo-target{cursor:pointer;box-sizing:border-box;border:3px solid transparent;transition:border-color .12s,box-shadow .12s;}',
    '.pct-eb-photo-target.is-hover{border-color:#d4af37;box-shadow:0 0 0 2px rgba(212,175,55,0.3);}',
    '.pct-eb-photo-target.is-selected{border-color:#d4af37;box-shadow:0 0 0 3px rgba(212,175,55,0.45);}',
    '.pct-eb-select-spinner{position:absolute;top:50%;left:50%;width:34px;height:34px;margin:-17px 0 0 -17px;border-radius:50%;background:rgba(20,20,20,0.72);display:flex;align-items:center;justify-content:center;z-index:70;pointer-events:none;}',
    '.pct-eb-select-spinner-ring{width:16px;height:16px;border:2px solid rgba(255,255,255,0.25);border-top:2px solid #fff;border-radius:50%;animation:pct-eb-spin 0.8s linear infinite;}',
    '.pct-eb-swap-confirm-badge{position:absolute;top:50%;left:50%;width:30px;height:30px;margin:-15px 0 0 -15px;border-radius:50%;background:#d4af37;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 4px 8px rgba(0,0,0,0.2);z-index:70;pointer-events:none;animation:pct-eb-popin .3s cubic-bezier(0.175,0.885,0.32,1.275) forwards;}',

    '.pct-eb-right-panel{width:220px;flex-shrink:0;padding:16px 28px 28px 16px;display:flex;flex-direction:column;background:#fbfbfb;}',
    '.pct-eb-selection-empty{color:#999;font-size:13px;line-height:1.6;padding-top:8px;}',
    '.pct-eb-selection-panel{display:none;flex-direction:column;gap:14px;}',
    '.pct-eb-selection-panel.is-visible{display:flex;}',
    '.pct-eb-selection-thumb{position:relative;width:100%;aspect-ratio:1;border-radius:12px;border:2.5px solid #d4af37;box-shadow:0 8px 20px rgba(212,175,55,0.15);overflow:hidden;background:#f4f4f4;display:flex;align-items:center;justify-content:center;}',
    '.pct-eb-selection-thumb img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block;}',
    '.pct-eb-selection-text{color:#555;font-size:13px;font-weight:500;line-height:1.5;}',
    '.pct-eb-selection-text b{color:#1a1a1a;}',
    '.pct-eb-selection-cancel{background:#fff;border:1.5px solid #eaeaea;color:#555;border-radius:8px;padding:10px 16px;font-size:12.5px;font-weight:700;font-family:"Syne",sans-serif;cursor:pointer;transition:border-color .15s;}',
    '.pct-eb-selection-cancel:hover{border-color:#ccc;}',

    '.pct-eb-saving-lock{position:absolute;inset:0;z-index:99999998;display:none;align-items:flex-end;justify-content:center;padding-bottom:40px;background:rgba(251,251,251,0.55);cursor:default;}',
    '.pct-eb-saving-lock.is-visible{display:flex;}',
    '.pct-eb-saving-pill{display:flex;align-items:center;gap:12px;background:#1a1a1a;color:#fff;padding:12px 24px;border-radius:30px;font-size:13px;font-weight:600;font-family:"Neue Haas Grotesk",Helvetica,Arial,sans-serif;letter-spacing:0.5px;box-shadow:0 10px 30px rgba(0,0,0,0.25);}',
    '.pct-eb-saving-spinner{width:16px;height:16px;flex-shrink:0;border:2px solid rgba(255,255,255,0.2);border-top:2px solid #fff;border-radius:50%;animation:pct-eb-spin 1s linear infinite;}',

    '@media (max-width:980px){.pct-eb-sidebar{width:150px;}.pct-eb-right-panel{width:170px;}}'
  ].join('');
  document.head.appendChild(style);

  // ── Helpers reused/matched against getBlueprintPhotoObjects() in design-studio-modal.liquid ──
  function isPhotoObject(obj) {
    if (!obj || obj.type !== 'image') return false;
    if (obj.isPaper || obj.isPlaceholder || obj.isSticker || obj.isBackground || obj._isCropRect) return false;
    if (!obj.customUrl && !obj.customThumbSrc) return false;
    return true;
  }

  function photoOrientation(obj) {
    var w = Number(obj.originalWidth || obj.width || 1);
    var h = Number(obj.originalHeight || obj.height || 1);
    return w > h ? 'landscape' : 'portrait';
  }

  // ── Build overlay skeleton once ──────────────────────────────────────────
  function ensureBuilt() {
    if (_isBuilt) return;
    _overlay = document.createElement('div');
    _overlay.className = 'pct-eb-overlay';
    _overlay.id = 'pct-eb-overlay';

    var header = document.createElement('div');
    header.className = 'pct-eb-header';
    var title = document.createElement('div');
    title.className = 'pct-eb-title';
    title.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="9" height="9" rx="1"></rect><rect x="13" y="4" width="9" height="9" rx="1"></rect><path d="M6.5 7V4.5a1 1 0 0 1 1-1H11"></path><path d="M17.5 13v2.5a1 1 0 0 1-1 1H13"></path></svg> Edit Book';
    _closeBtn = document.createElement('button');
    _closeBtn.type = 'button';
    _closeBtn.className = 'pct-eb-close';
    _closeBtn.textContent = '✕ Close';
    _closeBtn.onclick = attemptClose;
    header.appendChild(title);
    header.appendChild(_closeBtn);
    _overlay.appendChild(header);

    var instructions = document.createElement('div');
    instructions.className = 'pct-eb-instructions';
    instructions.innerHTML =
      '<p class="pct-eb-note">Click a photo, then click another <b>same-orientation</b> photo to swap them. Portrait photos can only swap with portrait, landscape only with landscape.</p>';
    _overlay.appendChild(instructions);

    var body = document.createElement('div');
    body.className = 'pct-eb-body';

    // ── Left: sidebar (spread list + rearrange toggle) ──
    var sidebar = document.createElement('div');
    sidebar.className = 'pct-eb-sidebar';

    _rearrangeToggle = document.createElement('button');
    _rearrangeToggle.type = 'button';
    _rearrangeToggle.className = 'pct-eb-rearrange-toggle';
    _rearrangeToggle.textContent = 'Rearrange Spreads';
    _rearrangeToggle.onclick = function () { setRearranging(!_rearranging); };
    sidebar.appendChild(_rearrangeToggle);

    _rearrangeHint = document.createElement('div');
    _rearrangeHint.className = 'pct-eb-rearrange-hint';
    _rearrangeHint.textContent = 'Drag a spread to move it, or click two spreads to swap them. Click the book to finish.';
    sidebar.appendChild(_rearrangeHint);

    _sidebarScroll = document.createElement('div');
    _sidebarScroll.className = 'pct-eb-sidebar-scroll';
    _sidebarList = document.createElement('div');
    _sidebarScroll.appendChild(_sidebarList);
    sidebar.appendChild(_sidebarScroll);

    // Auto-scroll near the top/bottom edge while dragging (same technique as
    // the editor's own updateThumbStrip() in design-studio-modal.liquid).
    _sidebarScroll.addEventListener('dragover', function (e) {
      if (!_rearranging) return;
      var rect = _sidebarScroll.getBoundingClientRect();
      var edgeZone = 48;
      var scrollSpeed = 10;
      if (e.clientY < rect.top + edgeZone) _sidebarScroll.scrollTop -= scrollSpeed;
      else if (e.clientY > rect.bottom - edgeZone) _sidebarScroll.scrollTop += scrollSpeed;
    });

    body.appendChild(sidebar);

    // ── Center: book-styled stage ──
    _stageWrap = document.createElement('div');
    _stageWrap.className = 'pct-eb-stage-wrap';
    _book = document.createElement('div');
    _book.className = 'pct-eb-book';
    _stage = document.createElement('div');
    _stage.className = 'pct-eb-stage';
    var bookLighting = document.createElement('div');
    bookLighting.className = 'pct-eb-book-lighting';
    var spineLine = document.createElement('div');
    spineLine.className = 'pct-eb-spine-line';
    _book.appendChild(_stage);
    _book.appendChild(bookLighting);
    _book.appendChild(spineLine);
    _stageWrap.appendChild(_book);
    body.appendChild(_stageWrap);

    // Clicking the book while rearranging exits rearrange mode — same effect
    // as the old "Done Rearranging" button (no server save, just ends the mode).
    _stage.addEventListener('click', function () {
      if (_rearranging) setRearranging(false);
    });

    // ── Right: selection panel ──
    var rightPanel = document.createElement('div');
    rightPanel.className = 'pct-eb-right-panel';

    _selectionEmpty = document.createElement('div');
    _selectionEmpty.className = 'pct-eb-selection-empty';
    _selectionEmpty.textContent = 'Click a photo in the book to select it for swapping.';
    rightPanel.appendChild(_selectionEmpty);

    _selectionPanel = document.createElement('div');
    _selectionPanel.className = 'pct-eb-selection-panel';
    var thumbBox = document.createElement('div');
    thumbBox.className = 'pct-eb-selection-thumb';
    _selectionThumbImg = document.createElement('img');
    _selectionThumbImg.alt = 'Selected photo';
    thumbBox.appendChild(_selectionThumbImg);
    _selectionText = document.createElement('div');
    _selectionText.className = 'pct-eb-selection-text';
    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'pct-eb-selection-cancel';
    cancelBtn.textContent = '✕ Cancel Selection';
    cancelBtn.onclick = function () {
      _selectedPhoto = null;
      renderSidebar();
      renderStage();
      renderSelectionPanel();
    };
    _selectionPanel.appendChild(thumbBox);
    _selectionPanel.appendChild(_selectionText);
    _selectionPanel.appendChild(cancelBtn);
    rightPanel.appendChild(_selectionPanel);

    body.appendChild(rightPanel);

    _overlay.appendChild(body);

    _savingLock = document.createElement('div');
    _savingLock.className = 'pct-eb-saving-lock';
    _savingLock.innerHTML =
      '<div class="pct-eb-saving-pill"><div class="pct-eb-saving-spinner"></div><span>Saving...</span></div>';
    _overlay.appendChild(_savingLock);

    document.body.appendChild(_overlay);

    _overlay.addEventListener('click', function (e) {
      if (e.target === _overlay) attemptClose();
    });

    window.addEventListener('resize', function () {
      if (_overlay && _overlay.classList.contains('is-open')) applyStageScale();
    });

    _isBuilt = true;
  }

  function applyStageScale() {
    if (!_book || !_stageWrap) return;
    var w = _stageWrap.clientWidth;
    var h = _stageWrap.clientHeight;
    var scale = Math.min(w / STAGE_W, h / STAGE_H);
    if (!isFinite(scale) || scale <= 0) scale = 1;
    _book.style.transform = 'scale(' + scale + ')';
  }

  // ── Sidebar thumbnails (own independent renderer/cache) ──────────────────
  function renderSidebarThumbnail(spreadIndex) {
    var spreadData = window.spreadMemory && window.spreadMemory[spreadIndex];
    if (!spreadData || typeof fabric === 'undefined') return Promise.resolve(null);
    var scaleX = SIDEBAR_THUMB_W / STAGE_W;
    var scaleY = SIDEBAR_THUMB_H / STAGE_H;
    var paperColor = spreadData.paperColor || '#ffffff';
    return new Promise(function (resolve) {
      try {
        var staticCanvas = new fabric.StaticCanvas(null, { width: SIDEBAR_THUMB_W, height: SIDEBAR_THUMB_H, backgroundColor: paperColor });
        if (!spreadData.objects || !spreadData.objects.length) {
          staticCanvas.renderAll();
          var emptyUrl = staticCanvas.toDataURL({ format: 'jpeg', quality: 0.7 });
          staticCanvas.dispose();
          resolve(emptyUrl);
          return;
        }
        var clone = JSON.parse(JSON.stringify(spreadData));
        clone.objects.forEach(function (obj) {
          obj.left = obj.left * scaleX;
          obj.top = obj.top * scaleY;
          obj.scaleX = (obj.scaleX || 1) * scaleX;
          obj.scaleY = (obj.scaleY || 1) * scaleY;
          if (obj.strokeWidth) obj.strokeWidth = obj.strokeWidth * scaleX;
          if (obj.type === 'image') obj.crossOrigin = 'anonymous';
        });
        fabric.util.enlivenObjects(clone.objects, function (objects) {
          objects.forEach(function (o) { staticCanvas.add(o); });
          staticCanvas.renderAll();
          var dataUrl = staticCanvas.toDataURL({ format: 'jpeg', quality: 0.7 });
          staticCanvas.dispose();
          resolve(dataUrl);
        });
      } catch (e) { resolve(null); }
    });
  }

  function renderSidebar() {
    _sidebarList.innerHTML = '';
    var spreads = window.spreadMemory || [];
    _rearrangeToggle.disabled = spreads.length < 2;
    for (var i = 0; i < spreads.length; i++) {
      renderSidebarItem(spreads[i], i);
    }
  }

  function swapBadgeSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="17 1 21 5 17 9"></polyline><path d="M3 11V9a4 4 0 0 1 4-4h14"></path>' +
      '<polyline points="7 23 3 19 7 15"></polyline><path d="M21 13v2a4 4 0 0 1-4 4H3"></path>' +
      '</svg>';
  }

  function checkmarkSvg() {
    return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  }

  function swapSpreadPositions(a, b) {
    var tmp = window.spreadMemory[a];
    window.spreadMemory[a] = window.spreadMemory[b];
    window.spreadMemory[b] = tmp;
    var tmpThumb = _thumbCache[a];
    _thumbCache[a] = _thumbCache[b];
    _thumbCache[b] = tmpThumb;
    var tmpGen = _thumbGen[a];
    _thumbGen[a] = _thumbGen[b];
    _thumbGen[b] = tmpGen;
    var tmpPromise = _thumbPromise[a];
    _thumbPromise[a] = _thumbPromise[b];
    _thumbPromise[b] = tmpPromise;
  }

  function clearSpreadSelection() {
    if (_selectedSpreadIdx === null) return;
    _selectedSpreadIdx = null;
    var prev = _sidebarList.querySelector('.pct-eb-sidebar-item.is-rearrange-selected');
    if (prev) prev.classList.remove('is-rearrange-selected');
  }

  function flashSpreadSwapConfirmed(a, b) {
    [a, b].forEach(function (idx) {
      var item = _sidebarList.querySelector('.pct-eb-sidebar-item[data-idx="' + idx + '"]');
      if (!item) return;
      var badge = document.createElement('div');
      badge.className = 'pct-eb-swap-confirm-badge';
      badge.innerHTML = checkmarkSvg();
      item.appendChild(badge);
      setTimeout(function () { if (badge.parentNode) badge.parentNode.removeChild(badge); }, 900);
    });
  }

  function renderSidebarItem(spread, idx) {
    var item = document.createElement('div');
    var classes = ['pct-eb-sidebar-item'];
    if (!_rearranging && idx === _currentSpreadIndex) classes.push('is-active');
    if (!_rearranging && _selectedPhoto && _selectedPhoto.spreadIndex === idx) classes.push('has-selection');
    if (_rearranging && _selectedSpreadIdx === idx) classes.push('is-rearrange-selected');
    item.className = classes.join(' ');
    item.dataset.idx = idx;
    item.draggable = _rearranging;

    var img = document.createElement('img');
    img.alt = 'Spread ' + (idx + 1);
    var cached = _thumbCache[idx];
    if (cached) {
      img.src = cached;
    } else {
      var bg = (spread && spread.paperColor) || '#ffffff';
      img.src = 'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="' + SIDEBAR_THUMB_W + '" height="' + SIDEBAR_THUMB_H + '"><rect width="' + SIDEBAR_THUMB_W + '" height="' + SIDEBAR_THUMB_H + '" fill="' + bg + '"/></svg>'
      );
      // Every caller for the same spread shares one in-flight render (avoids
      // redundant Fabric renders/image fetches piling up from repeated
      // renderSidebar() calls), and the generation token discards a result
      // that resolves after the spread's content was invalidated out from
      // under it — otherwise a slow, stale render could overwrite a fresher
      // one purely by finishing last.
      var gen = _thumbGen[idx] || 0;
      var promise = _thumbPromise[idx];
      if (!promise) {
        promise = renderSidebarThumbnail(idx);
        _thumbPromise[idx] = promise;
      }
      promise.then(function (dataUrl) {
        if (_thumbPromise[idx] === promise) _thumbPromise[idx] = null;
        if (!dataUrl) return;
        if ((_thumbGen[idx] || 0) !== gen) return; // superseded by a newer change — discard
        _thumbCache[idx] = dataUrl;
        if (parseInt(item.dataset.idx, 10) === idx) img.src = dataUrl;
      }).catch(function () {
        if (_thumbPromise[idx] === promise) _thumbPromise[idx] = null;
      });
    }
    item.appendChild(img);

    var num = document.createElement('div');
    num.className = 'pct-eb-sidebar-num';
    num.textContent = String(idx + 1);
    item.appendChild(num);

    var marker = document.createElement('div');
    marker.className = 'pct-eb-sidebar-marker';
    marker.title = 'Selected photo is on this spread';
    item.appendChild(marker);

    var badge = document.createElement('div');
    badge.className = 'pct-eb-swap-badge';
    badge.innerHTML = swapBadgeSvg();
    item.appendChild(badge);

    item.onclick = function () {
      var currentIdx = parseInt(item.dataset.idx, 10);
      if (_rearranging) {
        if (_selectedSpreadIdx === null) {
          _selectedSpreadIdx = currentIdx;
          renderSidebar();
          return;
        }
        if (_selectedSpreadIdx === currentIdx) {
          clearSpreadSelection();
          return;
        }
        swapSpreadPositions(_selectedSpreadIdx, currentIdx);
        var a = _selectedSpreadIdx, b = currentIdx;
        _selectedSpreadIdx = null;
        renderSidebar();
        renderStage();
        flashSpreadSwapConfirmed(a, b);
        return;
      }
      if (_currentSpreadIndex === currentIdx) return;
      _currentSpreadIndex = currentIdx;
      renderSidebar();
      renderStage();
    };

    item.ondragstart = function (e) {
      if (!_rearranging) { e.preventDefault(); return; }
      // Dragging always takes priority — cancel any pending click-selection first.
      clearSpreadSelection();
      e.dataTransfer.setData('pct-eb-idx', String(idx));
    };
    item.ondragover = function (e) {
      if (!_rearranging) return;
      e.preventDefault();
      item.style.opacity = '0.5';
    };
    item.ondragleave = function () { item.style.opacity = ''; };
    item.ondrop = function (e) {
      if (!_rearranging) return;
      e.preventDefault();
      item.style.opacity = '';
      var from = parseInt(e.dataTransfer.getData('pct-eb-idx'), 10);
      var to = idx;
      if (isNaN(from) || from === to) return;
      var moved = window.spreadMemory.splice(from, 1)[0];
      window.spreadMemory.splice(to, 0, moved);
      var movedThumb = _thumbCache.splice(from, 1)[0];
      _thumbCache.splice(to, 0, movedThumb);
      var movedGen = _thumbGen.splice(from, 1)[0];
      _thumbGen.splice(to, 0, movedGen);
      var movedPromise = _thumbPromise.splice(from, 1)[0];
      _thumbPromise.splice(to, 0, movedPromise);
      renderSidebar();
      renderStage();
    };

    _sidebarList.appendChild(item);
  }

  // ── Rearrange mode toggle: enforces the mutual-exclusion rule ────────────
  function setRearranging(on) {
    _rearranging = on;
    if (on) {
      _selectedPhoto = null; // clears any active photo selection — see file header
    } else {
      _selectedSpreadIdx = null; // no dangling pending spread-swap selection
    }
    _rearrangeToggle.textContent = on ? 'Done Rearranging' : 'Rearrange Spreads';
    _rearrangeToggle.classList.toggle('is-active', on);
    _rearrangeHint.classList.toggle('is-visible', on);
    _book.classList.toggle('is-locked', on);
    renderSidebar();
    renderStage();
    renderSelectionPanel();
  }

  // ── Main stage: render the current spread's real objects as click targets ──
  function renderStage() {
    _stage.innerHTML = '';
    var spread = (window.spreadMemory || [])[_currentSpreadIndex];
    _stage.style.background = (spread && spread.paperColor) || '#ffffff';
    var objects = (spread && spread.objects) || [];

    objects.forEach(function (obj, objectIndex) {
      if (!obj || obj.isPaper || obj._isCropRect) return;
      renderStageObject(obj, objectIndex);
    });

    applyStageScale();
  }

  function renderStageObject(obj, objectIndex) {
    var isPhoto = isPhotoObject(obj);
    // Photo boxes are rendered slightly oversized (matching the "bleed" convention
    // in getBoxes()/processPlaceholder() in design-studio-modal.liquid) so adjacent
    // photos overlap by a hair instead of leaving a sub-pixel seam under the stage's
    // scale-down transform.
    var bleed = isPhoto ? 2 : 0;
    var w = (Number(obj.width) || 0) * (Number(obj.scaleX) || 1) + bleed;
    var h = (Number(obj.height) || 0) * (Number(obj.scaleY) || 1) + bleed;
    var left = (Number(obj.left) || 0) - w / 2;
    var top = (Number(obj.top) || 0) - h / 2;

    var el = document.createElement('div');
    el.className = 'pct-eb-obj';
    el.dataset.objectIndex = objectIndex;
    el.style.left = left + 'px';
    el.style.top = top + 'px';
    el.style.width = w + 'px';
    el.style.height = h + 'px';
    if (obj.angle) el.style.transform = 'rotate(' + obj.angle + 'deg)';
    el.style.overflow = 'hidden';

    if (obj.type === 'image' && (obj.customUrl || obj.customThumbSrc)) {
      var img = document.createElement('img');
      img.src = obj.customThumbSrc || obj.customUrl;
      var srcW = Number(obj.originalWidth) || Number(obj.width) || 1;
      var srcH = Number(obj.originalHeight) || Number(obj.height) || 1;
      var sx = Number(obj.scaleX) || 1;
      var sy = Number(obj.scaleY) || 1;
      var cropX = Number(obj.cropX) || 0;
      var cropY = Number(obj.cropY) || 0;
      img.style.left = (-cropX * sx) + 'px';
      img.style.top = (-cropY * sy) + 'px';
      img.style.width = (srcW * sx) + 'px';
      img.style.height = (srcH * sy) + 'px';
      el.appendChild(img);
    } else if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox' || obj.isText) {
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      var span = document.createElement('div');
      span.textContent = obj.text || '';
      var fs = Math.max(6, (Number(obj.fontSize) || 16) * (Number(obj.scaleY) || 1));
      span.style.cssText = 'color:' + (obj.fill || '#000') + ';font-size:' + fs + 'px;font-family:' + (obj.fontFamily || 'inherit') + ';text-align:center;width:100%;white-space:pre-wrap;pointer-events:none;';
      el.appendChild(span);
    } else if (obj.customUrl || obj.customThumbSrc || obj.src) {
      var genImg = document.createElement('img');
      genImg.src = obj.customThumbSrc || obj.customUrl || obj.src;
      genImg.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;object-fit:contain;';
      el.appendChild(genImg);
    } else {
      el.style.background = obj.fill || 'rgba(0,0,0,0.06)';
    }

    if (isPhoto) {
      el.classList.add('pct-eb-photo-target');
      var isSelectedHere = _selectedPhoto && _selectedPhoto.spreadIndex === _currentSpreadIndex && _selectedPhoto.objectIndex === objectIndex;
      if (isSelectedHere) el.classList.add('is-selected');

      var badge = document.createElement('div');
      badge.className = 'pct-eb-swap-badge';
      badge.innerHTML = swapBadgeSvg();
      el.appendChild(badge);

      if (!_rearranging) {
        el.addEventListener('mouseenter', function () { el.classList.add('is-hover'); });
        el.addEventListener('mouseleave', function () { el.classList.remove('is-hover'); });
        el.addEventListener('click', function (e) {
          e.stopPropagation(); // don't let this bubble to the stage's exit-rearrange listener
          handlePhotoClick(_currentSpreadIndex, objectIndex, el);
        });
      }
    }

    _stage.appendChild(el);
  }

  // ── Right panel: selected photo (static box, contain-fit for either orientation) ──
  function renderSelectionPanel() {
    if (!_selectedPhoto) {
      _selectionEmpty.style.display = '';
      _selectionPanel.classList.remove('is-visible');
      return;
    }
    var obj = window.spreadMemory[_selectedPhoto.spreadIndex].objects[_selectedPhoto.objectIndex];
    var orientation = photoOrientation(obj);
    _selectionEmpty.style.display = 'none';
    _selectionPanel.classList.add('is-visible');
    _selectionThumbImg.src = obj.customThumbSrc || obj.customUrl;
    _selectionText.innerHTML = 'Selected — click a <b>' + orientation + '</b> photo on any spread to swap, or cancel below.';
  }

  // ── Transient feedback: brief spinner on select, checkmark pop on swap ───
  function showSelectSpinner(el, onDone) {
    var spinner = document.createElement('div');
    spinner.className = 'pct-eb-select-spinner';
    var ring = document.createElement('div');
    ring.className = 'pct-eb-select-spinner-ring';
    spinner.appendChild(ring);
    el.appendChild(spinner);
    setTimeout(onDone, 350);
  }

  function flashSwapConfirmed(selA, selB) {
    [selA, selB].forEach(function (sel) {
      if (sel.spreadIndex !== _currentSpreadIndex) return;
      var el = _stage.querySelector('.pct-eb-obj[data-object-index="' + sel.objectIndex + '"]');
      if (!el) return;
      var badge = document.createElement('div');
      badge.className = 'pct-eb-swap-confirm-badge';
      badge.innerHTML = checkmarkSvg();
      el.appendChild(badge);
      setTimeout(function () { if (badge.parentNode) badge.parentNode.removeChild(badge); }, 900);
    });
  }

  // ── Click handling: select / deselect / validate orientation / swap ──────
  function handlePhotoClick(spreadIndex, objectIndex, el) {
    clearSpreadSelection(); // mutual exclusion — see file header
    if (!_selectedPhoto) {
      showSelectSpinner(el, function () {
        _selectedPhoto = { spreadIndex: spreadIndex, objectIndex: objectIndex };
        renderSidebar();
        renderStage();
        renderSelectionPanel();
      });
      return;
    }
    if (_selectedPhoto.spreadIndex === spreadIndex && _selectedPhoto.objectIndex === objectIndex) {
      _selectedPhoto = null;
      renderSidebar();
      renderStage();
      renderSelectionPanel();
      return;
    }
    var objA = window.spreadMemory[_selectedPhoto.spreadIndex].objects[_selectedPhoto.objectIndex];
    var objB = window.spreadMemory[spreadIndex].objects[objectIndex];
    var orientA = photoOrientation(objA);
    var orientB = photoOrientation(objB);
    if (orientA !== orientB) {
      var msg = 'Please select a ' + orientA + ' photo to swap with this one.';
      if (window.showToast) window.showToast(msg, 4000, 'warn');
      else alert(msg);
      // Selection stays active on purpose — no state change here.
      return;
    }
    var selA = _selectedPhoto;
    var selB = { spreadIndex: spreadIndex, objectIndex: objectIndex };
    performSwap(selA, selB);
    _selectedPhoto = null;
    // Invalidate cached sidebar thumbnails for both affected spreads — their
    // content changed even though their position didn't, so the cache (which
    // is only kept in sync across spread reorders) would otherwise keep
    // showing the pre-swap composite until the tool is closed and reopened.
    // Bumping the generation token also discards any render for these spreads
    // that was already in flight before the swap, so a slow stale result
    // can't land after the fresh one and overwrite it.
    _thumbCache[selA.spreadIndex] = null;
    _thumbCache[selB.spreadIndex] = null;
    _thumbGen[selA.spreadIndex] = (_thumbGen[selA.spreadIndex] || 0) + 1;
    _thumbGen[selB.spreadIndex] = (_thumbGen[selB.spreadIndex] || 0) + 1;
    // Also drop any in-flight promise reference for these spreads — otherwise
    // the next render would subscribe to that same (now-stale) render instead
    // of starting a fresh one, capturing today's gen and defeating the check.
    _thumbPromise[selA.spreadIndex] = null;
    _thumbPromise[selB.spreadIndex] = null;
    renderSidebar();
    renderStage();
    renderSelectionPanel();
    flashSwapConfirmed(selA, selB);
  }

  // ── The photo swap itself ─────────────────────────────────────────────────
  function slotBoxSize(obj) {
    return {
      w: (Number(obj.width) || 0) * (Number(obj.scaleX) || 1),
      h: (Number(obj.height) || 0) * (Number(obj.scaleY) || 1)
    };
  }

  function computeCropForTarget(obj, targetW, targetH) {
    var naturalW = Number(obj.originalWidth) || Number(obj.width) || 1;
    var naturalH = Number(obj.originalHeight) || Number(obj.height) || 1;
    var scale = Math.max(targetW / naturalW, targetH / naturalH);
    var cropW = targetW / scale;
    var cropH = targetH / scale;
    return {
      scaleX: scale,
      scaleY: scale,
      width: cropW,
      height: cropH,
      cropX: Math.max(0, (naturalW - cropW) / 2),
      cropY: Math.max(0, (naturalH - cropH) / 2)
    };
  }

  function extractPhotoData(obj) {
    return {
      customUrl: obj.customUrl,
      customThumbSrc: obj.customThumbSrc,
      crossOrigin: obj.crossOrigin,
      photoId: obj.photoId,
      rawPath: obj.rawPath,
      sourcePath: obj.sourcePath,
      printPath: obj.printPath,
      statusPath: obj.statusPath,
      originalWidth: obj.originalWidth,
      originalHeight: obj.originalHeight
    };
  }

  function applyPhotoDataToObject(obj, data, crop) {
    obj.customUrl = data.customUrl;
    obj.customThumbSrc = data.customThumbSrc;
    obj.crossOrigin = data.crossOrigin || 'anonymous';
    obj.photoId = data.photoId || '';
    obj.rawPath = data.rawPath || '';
    obj.sourcePath = data.sourcePath || '';
    obj.printPath = data.printPath || '';
    obj.statusPath = data.statusPath || '';
    obj.originalWidth = data.originalWidth;
    obj.originalHeight = data.originalHeight;
    obj.scaleX = crop.scaleX;
    obj.scaleY = crop.scaleY;
    obj.width = crop.width;
    obj.height = crop.height;
    obj.cropX = crop.cropX;
    obj.cropY = crop.cropY;
    // left/top/angle/originX/originY are intentionally untouched — the slot's
    // own position and rotation stay fixed, only the photo occupying it moves.
  }

  function performSwap(selA, selB) {
    var objA = window.spreadMemory[selA.spreadIndex].objects[selA.objectIndex];
    var objB = window.spreadMemory[selB.spreadIndex].objects[selB.objectIndex];

    var boxA = slotBoxSize(objA);
    var boxB = slotBoxSize(objB);

    var cropForB_inA = computeCropForTarget(objB, boxA.w, boxA.h);
    var cropForA_inB = computeCropForTarget(objA, boxB.w, boxB.h);

    var dataA = extractPhotoData(objA);
    var dataB = extractPhotoData(objB);

    applyPhotoDataToObject(objA, dataB, cropForB_inA);
    applyPhotoDataToObject(objB, dataA, cropForA_inB);
  }

  // ── Persistence: one snapshot/save/discard flow covering both reordering
  //    and swapping done in the same session ──────────────────────────────
  function hasChanges() {
    try {
      return JSON.stringify(window.spreadMemory || []) !== JSON.stringify(_snapshotMemory || []);
    } catch (e) {
      return true;
    }
  }

  function showSaveConfirm() {
    return new Promise(function (resolve) {
      var overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:99999999;background:rgba(0,0,0,0.42);display:flex;align-items:center;justify-content:center;padding:20px;font-family:inherit;';
      overlay.innerHTML =
        '<div role="dialog" aria-modal="true" style="width:min(420px,100%);background:#fff;border-radius:18px;box-shadow:0 24px 80px rgba(0,0,0,0.22);padding:28px;text-align:center;">' +
          '<h2 style="margin:0 0 10px;font-size:20px;font-weight:800;color:#111;">Save these changes?</h2>' +
          '<p style="margin:0 0 24px;color:#555;font-size:14px;line-height:1.5;">You made changes to your book. Save them, or discard and keep the previous version.</p>' +
          '<div style="display:flex;gap:10px;">' +
            '<button type="button" data-pct-eb-discard style="flex:1;border:1.5px solid #e5e7eb;background:#fff;border-radius:999px;padding:14px 18px;font-size:14px;font-weight:700;cursor:pointer;color:#374151;">Don’t Save</button>' +
            '<button type="button" data-pct-eb-save style="flex:1;border:0;background:#2C3E35;color:#fff;border-radius:999px;padding:14px 18px;font-size:14px;font-weight:800;cursor:pointer;">Save</button>' +
          '</div>' +
        '</div>';
      function close(result) { overlay.remove(); resolve(result); }
      overlay.querySelector('[data-pct-eb-discard]').addEventListener('click', function () { close(false); });
      overlay.querySelector('[data-pct-eb-save]').addEventListener('click', function () { close(true); });
      document.body.appendChild(overlay);
    });
  }

  function closeOverlayOnly() {
    if (_overlay) _overlay.classList.remove('is-open');
    document.removeEventListener('keydown', onEsc);
    _escAttached = false;
    _selectedPhoto = null;
    _rearranging = false;
    _selectedSpreadIdx = null;
  }

  async function attemptClose() {
    if (_saving || _confirmingClose) return;
    if (!hasChanges()) {
      closeOverlayOnly();
      return;
    }
    _confirmingClose = true;
    try {
      var shouldSave = await showSaveConfirm();
      if (shouldSave) {
        await saveChanges();
      } else {
        window.spreadMemory = JSON.parse(JSON.stringify(_snapshotMemory || []));
      }
      closeOverlayOnly();
    } finally {
      _confirmingClose = false;
    }
  }

  async function saveChanges() {
    if (window.pctCartEditMode) {
      // Cart-edit persistence goes through the secured /app-proxy/save-blueprint
      // flow (pctSubmitCartEdit, triggered by the page's own "Save Changes"
      // button) — not ensureBlueprintFinalizedForCheckout, which was built for
      // the fresh-design confirm flow and writes straight to Firebase Storage
      // with no lease/signature/version check. Calling it here would silently
      // wipe the blueprint_version tag the backend's conflict check depends
      // on, defeating that guard for the rest of this book's life. The main
      // Fabric.js editor already skips it the same way in cart-edit mode.
      // window.spreadMemory is already updated by the swap/reorder logic
      // above, so all that's needed is refreshing the on-page preview — the
      // customer still has to use the outer "Save Changes" button to persist.
      if (window.build3DFlipbook) { try { window.build3DFlipbook(); } catch (e) {} }
      if (window.updateStepper) { try { window.updateStepper(); } catch (e) {} }
      if (window.validateMainStep) { try { window.validateMainStep(); } catch (e) {} }
      return;
    }
    if (!window.ensureBlueprintFinalizedForCheckout) return;
    _saving = true;
    if (_closeBtn) { _closeBtn.disabled = true; _closeBtn.textContent = 'Saving...'; }
    if (_savingLock) _savingLock.classList.add('is-visible');
    try {
      window.blueprintFinalizeVersion = (window.blueprintFinalizeVersion || 0) + 1;
      var version = window.blueprintFinalizeVersion;
      window.designBlueprintFinalized = false;
      window.designBlueprintFinalizing = false;
      window.designBlueprintFinalizationPromise = null;
      var result = await window.ensureBlueprintFinalizedForCheckout({ silent: true, version: version });
      if (!result || !result.ok) {
        console.warn('Could not save book changes:', result && result.message);
        if (window.showToast) window.showToast((result && result.message) || 'Could not save the changes. Please try again.', 5000, 'error');
      }
    } catch (err) {
      console.error('Edit Book save failed:', err);
      if (window.showToast) window.showToast('Could not save the changes. Please try again.', 5000, 'error');
    } finally {
      _saving = false;
      if (_closeBtn) { _closeBtn.disabled = false; _closeBtn.textContent = '✕ Close'; }
      if (_savingLock) _savingLock.classList.remove('is-visible');
      if (window.build3DFlipbook) { try { window.build3DFlipbook(); } catch (e) {} }
      if (window.updateStepper) { try { window.updateStepper(); } catch (e) {} }
      if (window.validateMainStep) { try { window.validateMainStep(); } catch (e) {} }
    }
  }

  function onEsc(e) { if (e.key === 'Escape') attemptClose(); }

  window.pctOpenEditBook = function () {
    if (!window.spreadMemory || !window.spreadMemory.length) {
      if (window.showToast) window.showToast('Nothing to edit yet.', 3000, 'warn');
      return;
    }
    ensureBuilt();
    _currentSpreadIndex = 0;
    _selectedPhoto = null;
    _selectedSpreadIdx = null;
    _rearranging = false;
    _confirmingClose = false;
    _thumbCache = [];
    _thumbGen = [];
    _thumbPromise = [];
    _snapshotMemory = JSON.parse(JSON.stringify(window.spreadMemory));
    _rearrangeToggle.textContent = 'Rearrange Spreads';
    _rearrangeToggle.classList.remove('is-active');
    _rearrangeHint.classList.remove('is-visible');
    _book.classList.remove('is-locked');
    renderSidebar();
    renderStage();
    renderSelectionPanel();
    _overlay.classList.add('is-open');
    applyStageScale();
    if (!_escAttached) {
      document.addEventListener('keydown', onEsc);
      _escAttached = true;
    }
  };
})();
